/**
 * Google Event Service - Manages calendar events for TeeSync rounds
 *
 * This service calls edge functions to create/update/delete Google calendar events.
 * Token management (including refresh) is handled entirely server-side by the edge
 * functions using the shared tokenManager - the frontend never sees access tokens.
 */
import { supabase } from '../lib/supabase';

/**
 * Creates a Google calendar event for a round
 * @param {string} roundId - The round ID to create an event for
 * @returns {Promise<Object>} The created event details
 */
export const createGoogleEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    console.log('📅 createGoogleEvent: Invoking edge function with roundId:', roundId, typeof roundId);

    const { data, error } = await supabase.functions.invoke('create-google-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    console.log('📅 createGoogleEvent: Response data:', data);
    console.log('📅 createGoogleEvent: Response error:', error);

    if (error) {
      console.error('Error creating Google event:', error);
      // Try to extract the actual error message from the response context
      if (error.context) {
        try {
          const errorBody = await error.context.json();
          console.error('📅 Edge function error details:', errorBody);
          throw new Error(errorBody.error || error.message);
        } catch (parseError) {
          // If we can't parse the body, just throw the original error
          console.error('📅 Could not parse error body:', parseError);
        }
      }
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to create Google event');
    }

    console.log('✅ Google event created:', data.eventId);
    return data;
  } catch (error) {
    console.error('createGoogleEvent error:', error);
    throw error;
  }
};

/**
 * Updates an existing Google calendar event for a round
 * @param {string} roundId - The round ID to update the event for
 * @returns {Promise<Object>} The updated event details
 */
export const updateGoogleEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('update-google-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Error updating Google event:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to update Google event');
    }

    console.log('✅ Google event updated:', data.eventId);
    return data;
  } catch (error) {
    console.error('updateGoogleEvent error:', error);
    throw error;
  }
};

/**
 * Deletes a Google calendar event for a round
 * @param {string} roundId - The round ID to delete the event for
 * @returns {Promise<Object>} Success confirmation
 */
export const deleteGoogleEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('delete-google-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Error deleting Google event:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete Google event');
    }

    console.log('✅ Google event deleted');
    return data;
  } catch (error) {
    console.error('deleteGoogleEvent error:', error);
    throw error;
  }
};

/**
 * Checks if user has Google calendar connected and sync enabled
 * @returns {Promise<boolean>} True if user can sync to Google
 */
export const canSyncToGoogle = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('📅 canSyncToGoogle: No user found');
      return false;
    }

    const { data: connection, error } = await supabase
      .from('calendar_connections')
      .select('sync_enabled')
      .eq('user_id', user.id)
      .eq('provider', 'google')
      .single();

    if (error) {
      console.log('📅 canSyncToGoogle: Error fetching connection:', error.message);
      return false;
    }

    if (!connection) {
      console.log('📅 canSyncToGoogle: No Google connection found for user');
      return false;
    }

    console.log('📅 canSyncToGoogle: Connection found, sync_enabled =', connection.sync_enabled);
    return connection.sync_enabled === true;
  } catch (error) {
    console.error('canSyncToGoogle error:', error);
    return false;
  }
};

/**
 * Automatically syncs a round to Google calendar if conditions are met
 * This is a convenience function that combines the check and create operations
 * @param {string} roundId - The round ID to sync
 * @returns {Promise<Object|null>} The created event details or null if skipped
 */
export const autoSyncRoundToGoogle = async (roundId) => {
  try {
    console.log('📅 autoSyncRoundToGoogle called for round:', roundId);
    const canSync = await canSyncToGoogle();
    console.log('📅 canSyncToGoogle result:', canSync);

    if (!canSync) {
      console.log('⏭️  Google sync skipped: calendar not connected or sync disabled');
      return null;
    }

    console.log('📅 Attempting to create Google event for round:', roundId);
    const result = await createGoogleEvent(roundId);
    console.log('📅 createGoogleEvent result:', result);
    return result;
  } catch (error) {
    // Log the error but don't throw - we don't want calendar sync failures to break round acceptance
    console.warn('⚠️  Failed to auto-sync round to Google:', error.message);
    console.warn('⚠️  Full error:', error);
    return null;
  }
};
